import React, { useState, useMemo, useEffect } from 'react';
import api from '../api/api';
import Layout from '../components/Layout';
import AccountDetailModal from '../components/AccountDetailModal';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBuilding, faUser, faPhone, faEnvelope, faMapMarkerAlt,
    faSearch, faTimes, faSyncAlt, faSortAmountDown, faSortAmountUp,
    faLayerGroup, faTimesCircle
} from '@fortawesome/free-solid-svg-icons';

const LinkedInIcon = () => (
    <svg className="lead-linkedin-icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9v8.37H9.2V10.9H6.46M7.83 6.15a1.64 1.64 0 1 0 0 3.28 1.64 1.64 0 0 0 0-3.28z" />
    </svg>
);

const formatLinkedInUrl = (url) => {
    if (!url || !url.trim()) return '';
    const trimmed = url.trim();
    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
    }
    if (trimmed.startsWith('www.') || trimmed.startsWith('linkedin.com')) {
        return `https://${trimmed}`;
    }
    return `https://www.linkedin.com/in/${trimmed}`;
};

function Contacts() {
    const { getRbacParams, roleId, empId } = useAuth();
    const [contacts, setContacts] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('ALL');
    const [sortField, setSortField] = useState('accountName');
    const [sortOrder, setSortOrder] = useState('asc');
    const [entries, setEntries] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Modal State for Read View
    const [selectedContact, setSelectedContact] = useState(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);

    // 🔹 FETCH DATA (with RBAC access control)
    const fetchContacts = async () => {
        try {
            setLoading(true);
            const rbac = getRbacParams();
            const qstr = new URLSearchParams(rbac).toString();
            const res = await api.get(`/leads?${qstr}`);

            const formatted = (res.data || []).map(item => ({
                id: item.AccountID,
                accountName: item.AccountName || '',
                type: item.AccountType || 'Organization',
                contactPerson: item.ContactPerson || '',
                designation: item.ContactDesignation || '',
                phone1: item.CntPhone1 || '',
                email1: item.CntMail1 || '',
                phone2: item.CntPhone2 || '',
                email2: item.CntMail2 || '',
                city: item.City || '',
                linkedin: item.Linkedin || '',
                status: item.Status || 'Lead',
                priority: item.Priority || 'Medium',
                leadGenerator: item.LeadGenerator,
                leadGeneratorName: item.LeadGeneratorName,
                responsible: item.Responsible,
                responsibleName: item.ResponsibleName,
                assignedTo: item.AssignedTo,
                assignedToName: item.AssignedToName,
                notes: item.Notes || '',
                createdAt: item.CreatedAt,
                updatedAt: item.UpdatedAt
            }));

            setContacts(formatted);
        } catch (err) {
            console.error('Failed to fetch contacts:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const rbac = getRbacParams();
            const qstr = new URLSearchParams(rbac).toString();
            const res = await api.get(`/users?${qstr}`);
            setUsers(res.data || []);
        } catch (e) {
            console.error('Failed to fetch users:', e);
        }
    };

    useEffect(() => {
        fetchContacts();
        fetchUsers();
    }, [roleId, empId]);

    const handleRowClick = (contact) => {
        setSelectedContact(contact);
        setViewModalOpen(true);
    };

    // 🔍 FILTER
    const filteredData = useMemo(() => {
        return contacts.filter(contact => {
            if (typeFilter !== 'ALL' && contact.type !== typeFilter) {
                return false;
            }

            if (!search.trim()) return true;

            const q = search.toLowerCase();
            return (
                contact.accountName.toLowerCase().includes(q) ||
                contact.contactPerson.toLowerCase().includes(q) ||
                contact.designation.toLowerCase().includes(q) ||
                contact.phone1.toLowerCase().includes(q) ||
                contact.phone2.toLowerCase().includes(q) ||
                contact.email1.toLowerCase().includes(q) ||
                contact.email2.toLowerCase().includes(q) ||
                contact.city.toLowerCase().includes(q) ||
                (contact.linkedin && contact.linkedin.toLowerCase().includes(q))
            );
        });
    }, [search, contacts, typeFilter]);

    // 🔃 SORT
    const sortedData = useMemo(() => {
        if (!sortField) return filteredData;

        return [...filteredData].sort((a, b) => {
            const valA = String(a[sortField] || '').toLowerCase();
            const valB = String(b[sortField] || '').toLowerCase();

            if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
            if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filteredData, sortField, sortOrder]);

    // 📄 PAGINATION
    const totalPages = Math.ceil(sortedData.length / entries);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * entries;
        return sortedData.slice(start, start + entries);
    }, [sortedData, currentPage, entries]);

    return (
        <Layout>
            <div className="dome-dashboard-wrap">

                {/* 1. TOP HEADER */}
                <div className="dome-dash-header-row">
                    <div className="dome-dash-title-wrap">
                        <h1 className="dome-dash-h1">Contacts &amp; Key Persons</h1>
                    </div>
                    <div className="dome-dash-actions">
                        <button
                            type="button"
                            className="btn-dome-secondary"
                            title="Refresh contacts directory"
                            onClick={fetchContacts}
                            disabled={loading}
                        >
                            <FontAwesomeIcon icon={faSyncAlt} spin={loading} />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>

                {/* 2. COMPACT FILTER TOOLBAR STRIP */}
                <div className="dome-dash-subnav-strip dome-leads-subnav">
                    <div className="dome-leads-toolbar-left">
                        <div className="dome-period-pills">
                            <button
                                type="button"
                                className={`dome-period-btn ${typeFilter === 'ALL' ? 'active' : ''}`}
                                onClick={() => { setTypeFilter('ALL'); setCurrentPage(1); }}
                            >
                                <FontAwesomeIcon icon={faLayerGroup} className="icon-mr-4" />
                                All ({contacts.length})
                            </button>
                            <button
                                type="button"
                                className={`dome-period-btn ${typeFilter === 'Organization' ? 'active' : ''}`}
                                onClick={() => { setTypeFilter('Organization'); setCurrentPage(1); }}
                            >
                                <FontAwesomeIcon icon={faBuilding} className="icon-mr-4" />
                                Organizations ({contacts.filter(c => c.type === 'Organization').length})
                            </button>
                            <button
                                type="button"
                                className={`dome-period-btn ${typeFilter === 'Individual' ? 'active' : ''}`}
                                onClick={() => { setTypeFilter('Individual'); setCurrentPage(1); }}
                            >
                                <FontAwesomeIcon icon={faUser} className="icon-mr-4" />
                                Individuals ({contacts.filter(c => c.type === 'Individual').length})
                            </button>
                        </div>

                        <div className="dome-search-mini-box">
                            <FontAwesomeIcon icon={faSearch} className="dome-search-mini-icon" />
                            <input
                                type="text"
                                placeholder="Search by name, contact, email, LinkedIn..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="dome-search-mini-input"
                            />
                            {search && (
                                <button
                                    type="button"
                                    className="dome-search-clear-mini"
                                    onClick={() => { setSearch(''); setCurrentPage(1); }}
                                    title="Clear search"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="dome-leads-toolbar-right">
                        {(typeFilter !== 'ALL' || search) && (
                            <button
                                type="button"
                                className="dome-clear-filters-mini"
                                onClick={() => {
                                    setTypeFilter('ALL');
                                    setSearch('');
                                    setCurrentPage(1);
                                }}
                                title="Reset all active filters"
                            >
                                <FontAwesomeIcon icon={faTimesCircle} />
                                <span>Reset</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* 3. DOME CARD TABLE */}
                <div className="dome-dash-card">
                    <div className="dome-dash-card-header">
                        <h3 className="dome-dash-card-title">Key Personnel Directory</h3>
                        <div className="dome-table-header-controls">
                            <div className="dome-mini-filter-item">
                                <span className="dome-filter-mini-label">Sort:</span>
                                <select
                                    value={sortField}
                                    onChange={(e) => setSortField(e.target.value)}
                                    className="dome-mini-select"
                                >
                                    <option value="accountName">Account / Organization</option>
                                    <option value="contactPerson">Contact Person</option>
                                    <option value="designation">Designation</option>
                                    <option value="city">City</option>
                                    <option value="type">Account Type</option>
                                </select>
                                <button
                                    type="button"
                                    className="dome-sort-dir-mini"
                                    onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
                                    title={`Sort Order: ${sortOrder === 'asc' ? 'Ascending' : 'Descending'}`}
                                >
                                    <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortAmountDown : faSortAmountUp} />
                                </button>
                            </div>

                            <div className="dome-mini-filter-item">
                                <span className="dome-filter-mini-label">Show:</span>
                                <select
                                    value={entries}
                                    onChange={(e) => {
                                        setEntries(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className="dome-mini-select"
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="dome-dash-table-wrap">
                        <table className="dome-dash-table dome-contacts-table">
                            <thead>
                                <tr>
                                    <th>Account / Organization</th>
                                    <th>Type</th>
                                    <th>Key Contact Person</th>
                                    <th>Designation / Role</th>
                                    <th>Direct Phone</th>
                                    <th>Direct Email</th>
                                    <th>LinkedIn</th>
                                    <th>Location</th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="8" className="td-empty text-center">
                                            Loading contacts...
                                        </td>
                                    </tr>
                                ) : paginatedData.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="td-empty text-center">
                                            No contacts found
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedData.map((contact) => (
                                        <tr
                                            key={contact.id}
                                            className="clickable-row"
                                            onClick={() => handleRowClick(contact)}
                                            title="Click to view full contact details"
                                        >
                                            <td>
                                                <div className="dome-lead-name-col">
                                                    <span className="dome-lead-name-link">
                                                        {contact.accountName}
                                                    </span>
                                                </div>
                                            </td>

                                            <td>
                                                {contact.type === 'Organization' ? (
                                                    <span>
                                                        Organization
                                                    </span>
                                                ) : (
                                                    <span>
                                                        Individual
                                                    </span>
                                                )}
                                            </td>

                                            <td>
                                                <span className="font-w-600">
                                                    {contact.type === 'Organization' ? (contact.contactPerson || '—') : contact.accountName}
                                                </span>
                                            </td>

                                            <td>
                                                <span className="contact-designation-text">
                                                    {contact.designation || (contact.type === 'Individual' ? 'Individual Practitioner' : 'Key Contact')}
                                                </span>
                                            </td>

                                            <td>
                                                {contact.phone1 ? (
                                                    <a
                                                        href={`tel:${contact.phone1}`}
                                                        className="lead-contact-link"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <FontAwesomeIcon icon={faPhone} className="contact-link-icon" />
                                                        {contact.phone1}
                                                    </a>
                                                ) : <span className="text-muted-dash">—</span>}
                                            </td>

                                            <td>
                                                {contact.email1 ? (
                                                    <a
                                                        href={`mailto:${contact.email1}`}
                                                        className="lead-contact-link"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <FontAwesomeIcon icon={faEnvelope} className="contact-link-icon" />
                                                        {contact.email1}
                                                    </a>
                                                ) : <span className="text-muted-dash">—</span>}
                                            </td>

                                            <td>
                                                {contact.linkedin ? (
                                                    <a
                                                        href={formatLinkedInUrl(contact.linkedin)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="lead-linkedin-link"
                                                        title={contact.linkedin}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        LinkedIn
                                                    </a>
                                                ) : (
                                                    <span className="text-muted-dash">—</span>
                                                )}
                                            </td>

                                            <td>
                                                {contact.city ? (
                                                    <span>
                                                        <FontAwesomeIcon icon={faMapMarkerAlt} className="contact-location-icon" />
                                                        {contact.city}
                                                    </span>
                                                ) : <span className="text-muted-dash">—</span>}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* DOME Table Footer Pagination */}
                    <div className="dome-table-footer">
                        <span className="dome-table-counter">
                            Showing {paginatedData.length === 0 ? 0 : (currentPage - 1) * entries + 1}&ndash;{Math.min(currentPage * entries, sortedData.length)} of {sortedData.length} entries
                        </span>

                        <div className="dome-pagination-controls">
                            <button
                                type="button"
                                className="dome-page-btn"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                            >
                                Prev
                            </button>

                            <span className="dome-page-indicator">Page {currentPage} of {totalPages || 1}</span>

                            <button
                                type="button"
                                className="dome-page-btn"
                                disabled={currentPage === totalPages || totalPages === 0}
                                onClick={() => setCurrentPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>

                {/* ================= SalesVault READ VIEW MODAL ================= */}
                <AccountDetailModal
                    isOpen={viewModalOpen}
                    account={selectedContact}
                    users={users}
                    onClose={() => setViewModalOpen(false)}
                />

            </div>
        </Layout>
    );
}

export default Contacts;